import React, { useMemo, useState } from "react";

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { MetricDef } from "consts";

import { ValidationNumericField, ValidationRow } from "models";

import { AntModal, Option, Select } from "components";

import { YieldByCropChart } from "pages/admin/soilSight/components/single/YieldByCropChart.tsx";
import { useValidationData } from "pages/admin/soilSight/hooks/useValidationData";
import { ChartTooltipProps } from "pages/admin/soilSight/utils/compareTypes.ts";
import { getScenarioColor } from "pages/admin/soilSight/utils/compareUtils.ts";

// `key` is unique (React keys, chart dataKeys); `field` names the row field when it differs, e.g. annual views of a cumulative metric.
type ChartMetricDef = Omit<MetricDef, "key" | "higherIsBetter"> & { description?: string; lowerIsBetter?: true } & (
    | { key: ValidationNumericField; field?: undefined }
    | { key: string; field: ValidationNumericField }
  );

const rowFieldOf = (metric: ChartMetricDef): ValidationNumericField => (metric.field === undefined ? metric.key : metric.field);

const CM_MODEL_OUTCOME_METRICS: ChartMetricDef[] = [
  {
    key: "cm_total",
    label: "CM Total (cumulative)",
    unit: "€/ha",
    description: "Cumulative contribution margin over time (running sum of annual values)",
    cumulative: true,
  },
  { key: "cm_income", label: "CM Income (cumulative)", unit: "€/ha", description: "Cumulative gross income from crop sales", cumulative: true },
  { key: "cm_costs", label: "CM Costs (cumulative)", unit: "€/ha", description: "Cumulative variable production costs", cumulative: true },
  {
    key: "cm_coverCropCosts",
    label: "CM Cover Crop Costs (cumulative)",
    unit: "€/ha",
    description: "Cumulative costs from cover crop establishment",
    cumulative: true,
  },
  { key: "cm_total_annual", field: "cm_total", label: "CM Total (annual)", unit: "€/ha", description: "Contribution margin for that single year" },
  {
    key: "cm_income_annual",
    field: "cm_income",
    label: "CM Income (annual)",
    unit: "€/ha",
    description: "Gross income from crop sales for that single year",
  },
  {
    key: "cm_costs_annual",
    field: "cm_costs",
    label: "CM Costs (annual)",
    unit: "€/ha",
    description: "Variable production costs for that single year",
  },
  {
    key: "cm_coverCropCosts_annual",
    field: "cm_coverCropCosts",
    label: "CM Cover Crop Costs (annual)",
    unit: "€/ha",
    description: "Cover crop establishment costs for that single year",
  },
  {
    key: "ss_cropYieldFractionOfTarget",
    label: "Yield vs Target",
    unit: "score",
    description: "Predicted yield as a fraction of the model's target for the crop (higher = closer to target)",
  },
  {
    key: "ss_cropYieldTarget",
    label: "Yield Target",
    unit: "t/ha",
    description: "Predicted absolute yield for the crop (v5 datasets only — always empty on v3/v4)",
  },
  { key: "ss_yieldStability", label: "Yield Stability", unit: "score", description: "Consistency of yield across years (higher = less variable)" },
  {
    key: "ss_soilAwcNormalised",
    label: "Available Water",
    unit: "score",
    description: "Normalised available water capacity (higher = better moisture holding)",
  },
  {
    key: "ss_pestLossAvoided",
    label: "Pest Loss Avoided",
    unit: "score",
    description: "Crop loss avoided through natural pest suppression (higher = more loss avoided)",
  },
  {
    key: "ss_nResilience",
    label: "N Resilience",
    unit: "score",
    description: "Resilience of the soil nitrogen supply (higher = more N available to the crop)",
  },
  {
    key: "ss_leaching",
    label: "Leaching",
    unit: "score",
    description: "Nutrient loss through drainage water (lower = less pollution risk)",
    lowerIsBetter: true,
  },
  {
    key: "ss_runoff",
    label: "Runoff",
    unit: "score",
    description: "Surface water runoff carrying soil and nutrients (lower = less erosion risk)",
    lowerIsBetter: true,
  },
  {
    key: "ss_waterErosion",
    label: "Water Erosion",
    unit: "score",
    description: "Soil loss driven by rainfall and runoff (lower = less soil degradation)",
    lowerIsBetter: true,
  },
  {
    key: "ss_windErosion",
    label: "Wind Erosion",
    unit: "score",
    description: "Soil loss driven by wind (lower = less soil degradation). Zero on v3/v4, which report no wind figure.",
    lowerIsBetter: true,
  },
];

type Props = { rows: ValidationRow[]; metrics?: ChartMetricDef[] };

type CustomTooltipProps = ChartTooltipProps<number> & { metric: ChartMetricDef };

const CustomTooltip = ({ active, payload, label, metric }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="soil-sight-tooltip">
      <div className="soil-sight-tooltip-year">Year {label}</div>
      {payload.map((entry) => {
        const scenario = String(entry.name ?? "").replace(`__${metric.key}`, "");
        return (
          <div key={scenario} className="soil-sight-tooltip-row">
            <span className="soil-sight-tooltip-dot" style={{ background: getScenarioColor(scenario, 0) }} />
            <span className="soil-sight-tooltip-name">{scenario}</span>
            <span className="soil-sight-tooltip-value">{entry.value != null ? `${Number(entry.value).toFixed(2)} ${metric.unit}` : "—"}</span>
          </div>
        );
      })}
    </div>
  );
};

const ChartContent = ({
  metric,
  chartData,
  scenarios,
  height,
  fontSize,
}: {
  metric: ChartMetricDef;
  chartData: Record<string, number | string | null>[];
  scenarios: string[];
  height: number;
  fontSize: number;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: -8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
      <XAxis dataKey="year" tick={{ fontSize }} label={{ value: "Year", position: "insideBottomRight", offset: -4, fontSize }} />
      <YAxis tick={{ fontSize }} />
      <Tooltip content={<CustomTooltip metric={metric} />} />
      <Legend
        content={() => (
          <div className="soil-sight-charts-legend">
            {scenarios.map((scenario, i) => (
              <span key={scenario} className="soil-sight-charts-legend-item">
                <span className="soil-sight-charts-legend-swatch" style={{ background: getScenarioColor(scenario, i) }} />
                {scenario}
              </span>
            ))}
          </div>
        )}
      />
      {scenarios.map((scenario, i) => (
        <Line
          key={scenario}
          type="monotone"
          dataKey={`${scenario}__${metric.key}`}
          stroke={getScenarioColor(scenario, i)}
          strokeWidth={2}
          dot={false}
          name={scenario}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

export const SingleVersionCharts = ({ rows, metrics = CM_MODEL_OUTCOME_METRICS }: Props) => {
  const { locations, rotations, scenarios } = useValidationData(rows);

  const rotationCrops = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of rows) if (typeof row.rotationCrops === "string") m.set(row.rotation, row.rotationCrops);
    return m;
  }, [rows]);

  const [location, setLocation] = useState(locations[0] ?? "");
  const [rotation, setRotation] = useState(rotations[0] ?? "");
  const [zoomed, setZoomed] = useState<ChartMetricDef | null>(null);

  const maxYear = useMemo(() => Math.max(...rows.map((r) => r.year), 20), [rows]);

  const chartData = useMemo(() => {
    const running: Record<string, number> = {};
    return Array.from({ length: maxYear }, (_, i) => {
      const year = i + 1;
      const point: Record<string, number | string | null> = { year };
      for (const scenario of scenarios) {
        const row = rows.find((r) => r.location === location && r.rotation === rotation && r.scenario === scenario && r.year === year);
        for (const m of metrics) {
          const dataKey = `${scenario}__${m.key}`;
          const annual = row ? row[rowFieldOf(m)] ?? null : null;
          if (m.cumulative) {
            running[dataKey] = (running[dataKey] ?? 0) + (annual ?? 0);
            point[dataKey] = Math.round(running[dataKey]);
          } else {
            point[dataKey] = annual;
          }
        }
      }
      return point;
    });
  }, [rows, location, rotation, scenarios, maxYear, metrics]);

  return (
    <div>
      <div className="soil-sight-charts-toolbar">
        <Select value={location} style={{ width: 160 }} onChange={setLocation}>
          {locations.map((l) => (
            <Option key={l} value={l}>
              {l}
            </Option>
          ))}
        </Select>
        <Select value={rotation} style={{ width: 340 }} onChange={setRotation}>
          {rotations.map((r) => (
            <Option key={r} value={r}>
              {rotationCrops.get(r) ? `${r} (${rotationCrops.get(r)})` : r}
            </Option>
          ))}
        </Select>
        <div className="soil-sight-charts-legend">
          {scenarios.map((scenario, i) => (
            <span key={scenario} className="soil-sight-charts-legend-item">
              <span className="soil-sight-charts-legend-swatch" style={{ background: getScenarioColor(scenario, i) }} />
              {scenario}
            </span>
          ))}
        </div>
      </div>

      {metrics === CM_MODEL_OUTCOME_METRICS && (
        <div className="soil-sight-charts-grid">
          <YieldByCropChart rows={rows} location={location} rotation={rotation} scenarios={scenarios} />
        </div>
      )}

      <div className="soil-sight-charts-grid">
        {metrics.map((metric) => (
          <div key={metric.key} onClick={() => setZoomed(metric)} className="soil-sight-chart-card">
            <div className="soil-sight-chart-card-header">
              {metric.label}
              <span className="soil-sight-chart-unit-badge">{metric.unit}</span>
              {metric.lowerIsBetter !== undefined && (
                <span className={`soil-sight-chart-direction-badge soil-sight-chart-direction-badge--${metric.lowerIsBetter ? "lower" : "higher"}`}>
                  {metric.lowerIsBetter ? "↓ lower is better" : "↑ higher is better"}
                </span>
              )}
            </div>
            {metric.description && <div className="soil-sight-chart-description">{metric.description}</div>}
            <ChartContent metric={metric} chartData={chartData} scenarios={scenarios} height={180} fontSize={9} />
          </div>
        ))}
      </div>

      <AntModal
        open={!!zoomed}
        onCancel={() => setZoomed(null)}
        afterOpenChange={(open) => {
          if (open) window.dispatchEvent(new Event("resize"));
        }}
        footer={null}
        width="80vw"
        title={
          zoomed ? (
            <div>
              <div className="soil-sight-modal-title-row">
                {zoomed.label}
                <span className="soil-sight-modal-unit-badge">{zoomed.unit}</span>
                {zoomed.lowerIsBetter !== undefined && (
                  <span className={`soil-sight-modal-direction-badge soil-sight-modal-direction-badge--${zoomed.lowerIsBetter ? "lower" : "higher"}`}>
                    {zoomed.lowerIsBetter ? "↓ lower is better" : "↑ higher is better"}
                  </span>
                )}
              </div>
              {zoomed.description && <div className="soil-sight-modal-subtitle">{zoomed.description}</div>}
            </div>
          ) : null
        }
        styles={{ body: { paddingTop: 8 } }}
      >
        {zoomed && <ChartContent metric={zoomed} chartData={chartData} scenarios={scenarios} height={420} fontSize={12} />}
      </AntModal>
    </div>
  );
};
